import { FormControlLabel } from '@mui/material';
import { CuriosityFilterByType, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Currency, DEFAULT_CURRENCY } from '@procureai/schemas';
import { CheckboxField } from '@procureai/ui-form-mui';
import {
  CuriosityComboboxField,
  CurrencyField,
  DateField,
  EnumSelectField,
  NumberField,
  PercentageField,
  TextAreaField,
  TextField,
} from '@procureai/ui-form-mui/fields';
import { useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  calculateBaseTotal,
  calculateNegotiatedTotal,
  calculateSavingRatio,
  calculateSavingTotal,
} from '../../utils/savingTotals';
import { SavingType } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useCurrentUser } from '~/hooks/useCurrentUser/useCurrentUser';

import {
  savingCostAllocationDistributionTypeOptions,
  getLongTermValidTo,
  savingCurrencyOptions,
  savingReductionTypeOptions,
  savingStatusOptions,
  savingTypeOptions,
  SavingFormData,
} from './SavingForm.schema';

const DECIMAL_SCALE = 2;

interface SavingFormProps {
  disabled?: boolean;
  loading?: boolean;
  mode: 'create' | 'edit';
}

export const SavingForm = ({ disabled = false, loading = false, mode }: SavingFormProps) => {
  const { t } = useTranslation(I18nNamespace.common, { keyPrefix: 'Savings.form.fields' });
  const { control, setValue, getValues } = useFormContext<SavingFormData>();
  const { userId } = useCurrentUser();
  const isDisabled = disabled || loading;
  const accountAssignmentCategory = useWatch({ control, name: 'accountAssignmentCategory' });
  const previousAccountAssignmentCategoryRef = useRef(accountAssignmentCategory);
  const buyerInitializedRef = useRef(false);
  const currency = useWatch({ control, name: 'currency' });
  const currencyCode = (currency as Currency) || DEFAULT_CURRENCY;
  const savingType = useWatch({ control, name: 'savingType' });

  const totalsRow = {
    baselinePrice: useWatch({ control, name: 'baselinePrice' }) ?? null,
    newPrice: useWatch({ control, name: 'newPrice' }) ?? null,
    baselineQuantity: useWatch({ control, name: 'baselineQuantity' }) ?? null,
    newQuantity: useWatch({ control, name: 'newQuantity' }) ?? null,
    originalPriceQuantity: useWatch({ control, name: 'originalPriceQuantity' }) ?? null,
    newPriceQuantity: useWatch({ control, name: 'newPriceQuantity' }) ?? null,
  };
  const baseTotal = calculateBaseTotal(totalsRow);
  const negotiatedTotal = calculateNegotiatedTotal(totalsRow);
  const savingTotal = calculateSavingTotal(totalsRow);
  const savingRatio = calculateSavingRatio(totalsRow);

  useEffect(() => {
    if (previousAccountAssignmentCategoryRef.current === accountAssignmentCategory) {
      return;
    }

    setValue('accountAssignmentValue', null, {
      shouldDirty: true,
      shouldValidate: true,
    });

    previousAccountAssignmentCategoryRef.current = accountAssignmentCategory;
  }, [accountAssignmentCategory, setValue]);

  useEffect(() => {
    if (mode !== 'create' || buyerInitializedRef.current) {
      return;
    }

    if (userId && !getValues('fkBuyer')) {
      setValue('fkBuyer', userId, { shouldDirty: false });
    }

    buyerInitializedRef.current = true;
  }, [mode, userId, getValues, setValue]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState: { invalid, error } }) => (
            <TextField
              label={`${t('name.label')} *`}
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
            />
          )}
        />

        {mode === 'edit' && (
          <Controller
            control={control}
            name="status"
            render={({ field, fieldState: { invalid, error } }) => (
              <EnumSelectField
                label={t('status.label')}
                options={savingStatusOptions}
                i18n={{ keyPrefix: 'SavingStatus' }}
                invalid={invalid}
                errors={[error]}
                disabled={isDisabled}
                {...field}
              />
            )}
          />
        )}

        {mode === 'edit' && savingType && (
          <EnumSelectField
            viewOnly
            label={t('savingType.label')}
            options={savingTypeOptions}
            i18n={{ keyPrefix: 'Savings.savingType' }}
            value={savingType}
          />
        )}
      </div>

      <Controller
        control={control}
        name="fkBuyer"
        render={({ field, fieldState: { invalid, error } }) => (
          <CuriosityComboboxField
            ns={CuriosityNamespaceType.INTERNAL_USER}
            label={t('buyer.label')}
            invalid={invalid}
            errors={[error]}
            disabled={isDisabled}
            {...field}
            value={field.value ?? null}
            onChange={(value) => field.onChange(value)}
          />
        )}
      />

      {mode === 'edit' && savingType === SavingType.AUTO && (
        <div className="grid grid-cols-1 gap-4">
          <Controller
            control={control}
            name="purchaseRequisition"
            render={({ field }) => (
              <TextField viewOnly label={t('purchaseRequisition.label')} value={field.value || null} />
            )}
          />
          <Controller
            control={control}
            name="shoppingCartId"
            render={({ field }) => <TextField viewOnly label={t('shoppingCartId.label')} value={field.value || null} />}
          />
          <Controller
            control={control}
            name="sourceReferenceItem"
            render={({ field }) => <TextField viewOnly label={t('item.label')} value={field.value || null} />}
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Controller
          control={control}
          name="currency"
          render={({ field, fieldState: { invalid, error } }) => (
            <EnumSelectField
              label={`${t('currency.label')} *`}
              options={savingCurrencyOptions}
              sortOptions={false}
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="reductionType"
          render={({ field, fieldState: { invalid, error } }) => (
            <EnumSelectField
              label={t('reductionType.label')}
              options={savingReductionTypeOptions}
              i18n={{ keyPrefix: 'SavingReductionType' }}
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? undefined}
            />
          )}
        />
      </div>

      <Controller
        control={control}
        name="description"
        render={({ field, fieldState: { invalid, error } }) => (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
            <div className="space-y-4">
              <Controller
                control={control}
                name="accountingAreaId"
                render={({
                  field: accountingAreaField,
                  fieldState: { invalid: accountingAreaInvalid, error: accountingAreaError },
                }) => (
                  <CuriosityComboboxField
                    ns={CuriosityNamespaceType.ACCOUNTING_AREA}
                    label={mode === 'edit' ? t('accountingArea.label') : `${t('accountingArea.label')} *`}
                    invalid={accountingAreaInvalid}
                    errors={[accountingAreaError]}
                    disabled={isDisabled || mode === 'edit'}
                    {...accountingAreaField}
                    value={accountingAreaField.value ?? null}
                    onChange={(value) => accountingAreaField.onChange(value)}
                  />
                )}
              />
              <Controller
                control={control}
                name="procurementOrganizationId"
                render={({
                  field: procurementOrganizationField,
                  fieldState: { invalid: procurementOrganizationInvalid, error: procurementOrganizationError },
                }) => (
                  <CuriosityComboboxField
                    ns={CuriosityNamespaceType.PROCUREMENT_ORGANIZATION}
                    label={`${t('procurementOrganization.label')} *`}
                    invalid={procurementOrganizationInvalid}
                    errors={[procurementOrganizationError]}
                    disabled={isDisabled}
                    {...procurementOrganizationField}
                    value={procurementOrganizationField.value ?? null}
                    onChange={(value) => procurementOrganizationField.onChange(value)}
                  />
                )}
              />
              <Controller
                control={control}
                name="organizationId"
                render={({
                  field: organizationField,
                  fieldState: { invalid: organizationInvalid, error: organizationError },
                }) => (
                  <CuriosityComboboxField
                    ns={CuriosityNamespaceType.ORGANIZATION}
                    label={`${t('organization.label')} *`}
                    invalid={organizationInvalid}
                    errors={[organizationError]}
                    disabled={isDisabled}
                    {...organizationField}
                    value={organizationField.value ?? null}
                    onChange={(value) => organizationField.onChange(value)}
                  />
                )}
              />
            </div>

            <TextAreaField
              label={t('description.label')}
              rows={8}
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? ''}
            />
          </div>
        )}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Controller
          control={control}
          name="commodityGroupId"
          render={({ field, fieldState: { invalid, error } }) => (
            <CuriosityComboboxField
              ns={CuriosityNamespaceType.COMMODITY_GROUP}
              label={`${t('commodityGroup.label')} *`}
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? null}
              onChange={(value) => field.onChange(value)}
            />
          )}
        />
        <Controller
          control={control}
          name="commodity"
          render={({ field, fieldState: { invalid, error } }) => (
            <TextField
              label={t('commodity.label')}
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? ''}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Controller
          control={control}
          name="purchasingGroupId"
          render={({ field, fieldState: { invalid, error } }) => (
            <CuriosityComboboxField
              ns={CuriosityNamespaceType.PURCHASING_GROUP}
              label={`${t('purchasingGroup.label')} *`}
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? null}
              onChange={(value) => field.onChange(value)}
            />
          )}
        />
        <Controller
          control={control}
          name="supplierId"
          render={({ field, fieldState: { invalid, error } }) => (
            <CuriosityComboboxField
              ns={CuriosityNamespaceType.SUPPLIER}
              label={`${t('supplier.label')} *`}
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? null}
              onChange={(value) => field.onChange(value)}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Controller
          control={control}
          name="baselinePrice"
          render={({ field, fieldState: { invalid, error } }) => (
            <CurrencyField
              label={`${t('baselinePrice.label')} *`}
              currency={currencyCode}
              allowNegative={false}
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? null}
            />
          )}
        />
        <Controller
          control={control}
          name="newPrice"
          render={({ field, fieldState: { invalid, error } }) => (
            <CurrencyField
              label={`${t('newPrice.label')} *`}
              currency={currencyCode}
              allowNegative={false}
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? null}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Controller
          control={control}
          name="baselineQuantity"
          render={({ field, fieldState: { invalid, error } }) => (
            <NumberField
              label={`${t('baselineQuantity.label')} *`}
              decimalScale={DECIMAL_SCALE}
              fixedDecimalScale
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? null}
            />
          )}
        />
        <Controller
          control={control}
          name="newQuantity"
          render={({ field, fieldState: { invalid, error } }) => (
            <NumberField
              label={`${t('newQuantity.label')} *`}
              decimalScale={DECIMAL_SCALE}
              fixedDecimalScale
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? null}
            />
          )}
        />
      </div>

      <Controller
        control={control}
        name="unitId"
        render={({ field, fieldState: { invalid, error } }) => (
          <CuriosityComboboxField
            ns={CuriosityNamespaceType.UNIT}
            label={t('unit.label')}
            invalid={invalid}
            errors={[error]}
            disabled={isDisabled}
            {...field}
            value={field.value ?? null}
            onChange={(value) => field.onChange(value)}
          />
        )}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Controller
          control={control}
          name="originalPriceQuantity"
          render={({ field, fieldState: { invalid, error } }) => (
            <NumberField
              label={t('originalPriceQuantity.label')}
              decimalScale={DECIMAL_SCALE}
              fixedDecimalScale
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? null}
            />
          )}
        />
        <Controller
          control={control}
          name="newPriceQuantity"
          render={({ field, fieldState: { invalid, error } }) => (
            <NumberField
              label={t('newPriceQuantity.label')}
              decimalScale={DECIMAL_SCALE}
              fixedDecimalScale
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? null}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <CurrencyField viewOnly currency={currencyCode} label={t('baseTotal.label')} value={baseTotal} />
        <CurrencyField viewOnly currency={currencyCode} label={t('negotiatedTotal.label')} value={negotiatedTotal} />
        <CurrencyField viewOnly currency={currencyCode} label={t('savingTotal.label')} value={savingTotal} />
        <PercentageField viewOnly label={t('savingPercent.label')} value={savingRatio} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Controller
          control={control}
          name="validFrom"
          render={({ field, fieldState: { invalid, error } }) => (
            <DateField
              label={t('validFrom.label')}
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              value={field.value ? new Date(field.value) : null}
              onChange={(value) => field.onChange(value?.toISOString() || null)}
            />
          )}
        />
        <Controller
          control={control}
          name="validTo"
          render={({ field }) => (
            <FormControlLabel
              label={t('longTerm.label')}
              control={
                <CheckboxField
                  disabled={isDisabled}
                  value={field.value != null}
                  onChange={(checked) => field.onChange(checked ? getLongTermValidTo() : null)}
                />
              }
            />
          )}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Controller
          control={control}
          name="distributionType"
          render={({ field, fieldState: { invalid, error } }) => (
            <EnumSelectField
              label={t('distributionType.label')}
              options={savingCostAllocationDistributionTypeOptions}
              i18n={{ keyPrefix: 'SavingCostAllocationDistributionType' }}
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? undefined}
            />
          )}
        />
        <Controller
          control={control}
          name="allocationValue"
          render={({ field, fieldState: { invalid, error } }) => (
            <NumberField
              label={t('allocationValue.label')}
              decimalScale={DECIMAL_SCALE}
              fixedDecimalScale
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? null}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Controller
          control={control}
          name="accountAssignmentCategory"
          render={({ field, fieldState: { invalid, error } }) => (
            <CuriosityComboboxField
              ns={CuriosityNamespaceType.ACCOUNT_ASSIGNMENT_CATEGORY}
              label={t('accountAssignmentCategory.label')}
              invalid={invalid}
              errors={[error]}
              disabled={isDisabled}
              {...field}
              value={field.value ?? null}
              onChange={(value) => field.onChange(value)}
            />
          )}
        />
        <Controller
          control={control}
          name="accountAssignmentValue"
          render={({ field, fieldState: { invalid, error } }) => {
            if (!accountAssignmentCategory) {
              return (
                <TextField
                  label={t('accountAssignmentValue.label')}
                  placeholder={t('accountAssignmentValue.selectCategoryFirst')}
                  disabled
                  invalid={invalid}
                  errors={[error]}
                  value=""
                  onChange={field.onChange}
                />
              );
            }

            return (
              <CuriosityComboboxField
                ns={CuriosityNamespaceType.ACCOUNT_ASSIGNMENT}
                label={t('accountAssignmentValue.label')}
                filters={[
                  { field: CuriosityFilterByType.ACCOUNT_ASSIGNMENT_CATEGORY_ID, value: accountAssignmentCategory },
                ]}
                invalid={invalid}
                errors={[error]}
                disabled={isDisabled}
                {...field}
                value={field.value ?? null}
                onChange={(value) => field.onChange(value)}
              />
            );
          }}
        />
      </div>

      <Controller
        control={control}
        name="glAccountId"
        render={({ field: glAccountField, fieldState: { invalid: glAccountInvalid, error: glAccountError } }) => (
          <CuriosityComboboxField
            ns={CuriosityNamespaceType.GL_ACCOUNT}
            label={t('glAccount.label')}
            invalid={glAccountInvalid}
            errors={[glAccountError]}
            disabled={isDisabled}
            {...glAccountField}
            value={glAccountField.value ?? null}
            onChange={(value) => glAccountField.onChange(value)}
          />
        )}
      />
    </div>
  );
};
